import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { SEO } from "@/components/SEO";
import { api } from "@/services/api";
import { useI18nStore } from "@/stores/i18nStore";

/**
 * The landing page for the link in the verification email.
 *
 * The confirmation itself happens on the backend - this page carries the token
 * from the URL to the API and reports what came back. It is reachable signed in
 * or signed out, because the link is opened from a mail client and there is no
 * telling which the reader is.
 */

type Status = "working" | "verified" | "expired" | "invalid" | "unavailable";

export function VerifyEmailPage() {
  const { t } = useI18nStore();
  const messages: Record<
    Exclude<Status, "working">,
    { title: string; body: string }
  > = {
    verified: {
      title: t.auth2EmailConfirmedTitle,
      body: t.auth2EmailConfirmedBody,
    },
    expired: {
      title: t.auth2VerifyExpiredTitle,
      body: t.auth2VerifyExpiredBody,
    },
    invalid: {
      title: t.auth2VerifyInvalidTitle,
      body: t.auth2VerifyInvalidBody,
    },
    unavailable: {
      title: t.auth2VerifyUnavailableTitle,
      body: t.auth2VerifyUnavailableBody,
    },
  };
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [status, setStatus] = useState<Status>("working");

  // A verification link is single use, so the request must not be repeated -
  // React runs effects twice in development, and a second call would report the
  // freshly spent token as invalid.
  const sent = useRef(false);

  useEffect(() => {
    if (sent.current) return;
    sent.current = true;

    if (!token) {
      setStatus("invalid");
      return;
    }

    api
      .post("/auth/verify-email", { token })
      .then(() => setStatus("verified"))
      .catch((error) => {
        const code = error?.response?.data?.error?.code;
        if (code === "VERIFICATION_EXPIRED") setStatus("expired");
        else if (code === "VERIFICATION_UNAVAILABLE") setStatus("unavailable");
        else if (code === "VERIFICATION_INVALID") setStatus("invalid");
        else setStatus("unavailable");
      });
  }, [token]);

  const message = status === "working" ? null : messages[status];

  return (
    <>
      <SEO title={t.auth2ConfirmYourEmail} />
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary-50 to-green-50 px-4">
        <div
          className="w-full max-w-md rounded-3xl bg-white/95 p-8 text-center shadow-2xl"
          data-testid="verify-email-panel"
        >
          {status === "working" ? (
            <>
              <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary-500" />
              <h1 className="mt-4 text-xl font-bold text-gray-900">
                {t.auth2ConfirmingEmail}
              </h1>
              <p className="mt-2 text-gray-500">{t.auth2OneMoment}</p>
            </>
          ) : (
            <>
              {status === "verified" ? (
                <CheckCircle2 className="mx-auto h-10 w-10 text-green-500" />
              ) : (
                <XCircle className="mx-auto h-10 w-10 text-red-500" />
              )}
              <h1 className="mt-4 text-xl font-bold text-gray-900">
                {message?.title}
              </h1>
              <p className="mt-2 text-gray-600">{message?.body}</p>
              <Link
                to="/traveler/login"
                className="mt-6 inline-block rounded-xl bg-gradient-to-r from-primary-500 to-green-500 px-6 py-3 font-semibold text-white"
              >
                {t.auth2GoToSignIn}
              </Link>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default VerifyEmailPage;
